import React, { useState, useEffect } from 'react';
import {
    FiUser, FiMail, FiPhone, FiBook, FiAward,
    FiCalendar, FiHash, FiBarChart2, FiLayers, FiActivity,
} from 'react-icons/fi';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const font = "'Outfit', sans-serif";
const C = {
    text: '#1a3326',
    sub: '#4a7060',
    light: '#8ab5a0',
    border: '#c8e6d8',
    primary: '#27a065',
    bg: '#edfaf4',
    white: '#ffffff',
};

const InfoRow = ({ icon, label, value }) => (
    <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 12,
        padding: '12px 16px', borderBottom: `1px solid ${C.border}`,
    }}>
        <div style={{
            width: 32, height: 32, borderRadius: 9, flexShrink: 0,
            background: 'rgba(39,160,101,0.10)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: C.primary, fontSize: 14,
        }}>{icon}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.light, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 2 }}>{label}</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.text, wordBreak: 'break-word' }}>{value || '—'}</div>
        </div>
    </div>
);

const StudentProfile = () => {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const { user } = useAuth();

    useEffect(() => {
        api.get('/api/student/profile')
            .then(res => setProfile(res.data.student))
            .catch(() => setProfile(null))
            .finally(() => setLoading(false));
    }, []);

    const p = profile || user || {};

    if (loading) return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 300, gap: 14 }}>
            <div style={{ width: 38, height: 38, border: `4px solid ${C.border}`, borderTopColor: C.primary, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <p style={{ fontSize: 13, color: C.sub, fontFamily: font }}>Loading profile…</p>
        </div>
    );

    const nameWords = (p.name || user?.name || 'S').split(' ');
    const initials = nameWords.map(n => n[0]).join('').toUpperCase().slice(0, 2);

    return (
        <div style={{ width: '100%', fontFamily: font }}>


            {/* ── Hero Card ── */}
            <div style={{
                background: 'linear-gradient(135deg, #d5f3d8 0%, #e8faee 60%, #f0fff4 100%)',
                borderRadius: 20, padding: '24px 20px 20px', marginBottom: 16,
                border: `1.5px solid ${C.border}`,
                boxShadow: '0 4px 20px rgba(39,160,101,0.10)',
            }}>
                {/* Avatar row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
                    <div style={{
                        width: 72, height: 72, borderRadius: '50%', flexShrink: 0,
                        background: 'linear-gradient(135deg, #6edba8, #27a065)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontSize: 26, fontWeight: 900,
                        boxShadow: '0 4px 16px rgba(39,160,101,0.30)',
                        border: '3px solid white',
                    }}>{initials}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <h1 style={{ fontSize: 20, fontWeight: 900, color: C.text, margin: '0 0 3px', letterSpacing: -0.4 }}>
                            {p.name || user?.name || 'Student'}
                        </h1>
                        <p style={{ fontSize: 12, color: C.sub, margin: '0 0 8px', lineHeight: 1.4 }}>
                            {p.course_name || user?.course || ''}{p.dept ? ` · ${p.dept}` : ''}
                        </p>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'rgba(39,160,101,0.12)', color: C.primary, fontSize: 11, fontWeight: 700, borderRadius: 20, padding: '3px 10px' }}>
                            🎓 Student
                        </span>
                    </div>
                </div>

                {/* Enrollment + Roll badges */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {(p.student_id || user?.student_id) && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'rgba(39,160,101,0.12)', color: C.primary, fontSize: 12, fontWeight: 700, borderRadius: 12, padding: '5px 12px' }}>
                            <FiHash size={10} /> {p.student_id || user?.student_id}
                        </span>
                    )}
                    {(p.roll_number || user?.roll_number) && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'rgba(39,160,101,0.07)', color: C.sub, fontSize: 12, fontWeight: 600, borderRadius: 12, padding: '5px 12px' }}>
                            Roll: {p.roll_number || user?.roll_number}
                        </span>
                    )}
                </div>
            </div>

            {/* ── Stat Grid ── */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 10, marginBottom: 16,
            }}>
                {[
                    { label: 'CGPA', value: p.cgpa ?? '—', color: '#27a065', icon: <FiAward size={14} /> },
                    { label: 'Attendance', value: p.attendance_percentage != null ? `${p.attendance_percentage}%` : '—', color: '#5ab4d4', icon: <FiActivity size={14} /> },
                    { label: 'Batch', value: p.batch_name || user?.batch || '—', color: '#9f7bd4', icon: <FiLayers size={14} />, small: true },
                ].map(stat => (
                    <div key={stat.label} style={{
                        borderRadius: 14, background: stat.color + '12',
                        border: `1.5px solid ${stat.color}30`,
                        padding: '14px 10px', textAlign: 'center',
                    }}>
                        <div style={{ color: stat.color, display: 'flex', justifyContent: 'center', marginBottom: 4 }}>{stat.icon}</div>
                        <div style={{
                            fontSize: stat.small ? 10 : 20, fontWeight: 900,
                            color: stat.color, lineHeight: 1.2,
                            wordBreak: 'break-word',
                        }}>{stat.value}</div>
                        <div style={{ fontSize: 9, fontWeight: 700, color: C.sub, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 4 }}>{stat.label}</div>
                    </div>
                ))}
            </div>

            {/* ── Info Sections stacked on mobile, side-by-side on desktop ── */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                gap: 14,
            }}>
                {[
                    {
                        title: 'Personal Info',
                        rows: [
                            { icon: <FiUser />, label: 'Full Name', value: p.name || user?.name },
                            { icon: <FiMail />, label: 'Email', value: p.email || user?.email },
                            { icon: <FiPhone />, label: 'Phone', value: p.phone_number },
                            { icon: <FiCalendar />, label: 'Father Name', value: p.father_name },
                            { icon: <FiUser />, label: 'Blood Group', value: p.blood_group },
                        ],
                    },
                    {
                        title: 'Academic Info',
                        rows: [
                            { icon: <FiBook />, label: 'Course', value: p.course_name || user?.course },
                            { icon: <FiLayers />, label: 'Department', value: p.dept || user?.department },
                            { icon: <FiAward />, label: 'Batch', value: p.batch_name || user?.batch },
                            { icon: <FiHash />, label: 'Enrollment No.', value: p.student_id || user?.student_id },
                            { icon: <FiBarChart2 />, label: 'Reg. Number', value: p.reg_number || user?.reg_number },
                        ],
                    },
                ].map(section => (
                    <div key={section.title} style={{
                        background: C.white, borderRadius: 16,
                        border: `1.5px solid ${C.border}`, overflow: 'hidden',
                        boxShadow: '0 3px 12px rgba(39,160,101,0.07)',
                    }}>
                        <div style={{
                            padding: '12px 16px', background: C.bg,
                            borderBottom: `1px solid ${C.border}`,
                            display: 'flex', alignItems: 'center', gap: 8,
                            fontWeight: 800, fontSize: 13, color: C.text,
                        }}>
                            <span style={{ width: 4, height: 16, borderRadius: 3, background: 'linear-gradient(180deg,#6edba8,#27a065)', display: 'inline-block' }} />
                            {section.title}
                        </div>
                        {section.rows.map((r, i) => (
                            <InfoRow key={i} {...r} />
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default StudentProfile;
